import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useNavigate, useSearchParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import NoticeSummaryTable, { NoticeSummaryTableVariant, getDefaultFilterByVariant } from "./NoticeSummaryTable";
import {
  useApplicableViolationTypes,
  useFeatureFlags,
  useIsMobile,
  useUserProfile,
} from "@app.automotus.io/components/hooks";
import NoticesInactive from "./NoticesInactive";
import { shouldShowInactiveNoticesPage } from "../../utils/userProfile";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import Container from "@mui/material/Container";
import { FEATURE_FLAGS } from "@curbsuite/constants";

export const Notices: React.FC = () => {
  const isMobile = useIsMobile();
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const isEligibleForManualCitations = isMobile && isEnabled(FEATURE_FLAGS.MANUAL_CITATIONS_IS_ENABLED);
  const [searchParams, setSearchParams] = useSearchParams({
    table: "pending",
  });
  const { userProfile } = useUserProfile();
  const tab = (searchParams.get("table") as NoticeSummaryTableVariant) || "pending";
  // Hoisted here (rather than in NoticeSummaryTable) so the query persists across the
  // key={tab} remount below — switching tabs just changes the $filter variable and lets
  // Apollo re-read/refetch normally, instead of tearing down and rebuilding the subscription.
  const { applicableViolationTypes } = useApplicableViolationTypes("notices", getDefaultFilterByVariant(tab));

  if (shouldShowInactiveNoticesPage(userProfile)) {
    return <NoticesInactive />;
  }

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    if (!newValue) {
      return;
    }
    searchParams.set("table", newValue);
    searchParams.delete("sort");
    searchParams.delete("page");
    searchParams.delete("filter");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flexGrow: 1,
        py: { xs: 1.5, sm: 3.5 },
      }}
    >
      <Stack direction="column" spacing={{ xs: 1.5, sm: 3 }} sx={{ height: "100%", flexGrow: 1 }}>
        <Box>
          {/* Only the (short, single-line) title shares a row with the button -- the subtitle
              below is unrelated to whether the button fits, so it can't be pushed into an
              awkward wrap by it. */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Typography variant={isSmallScreen ? "h4xs" : "h4"}>Notices</Typography>
            {isEligibleForManualCitations && (
              <Button variant="contained" onClick={() => navigate("create")}>
                Generate Citation
              </Button>
            )}
          </Stack>
          {!isSmallScreen && (
            <Typography variant="subtitle1">Notices 'pending' will not be issued to the curb violator.</Typography>
          )}
        </Box>
        {/*
          key={tab} forces a full remount on tab switch — each tab has its own tableName/
          defaultVisibilityModel (see NoticeSummaryTable), but DataGridPro's live
          columnVisibilityModel and DataTableContainer's once-only initialState snapshot don't
          reset on their own when only props change, so a column hidden on one tab would otherwise
          stay hidden after switching to a tab where it should default to visible.
        */}
        <NoticeSummaryTable
          key={tab}
          variant={tab}
          onChangeVariant={handleChangeTab}
          applicableViolationTypes={applicableViolationTypes}
        />
      </Stack>
    </Container>
  );
};

export default Notices;
